'use client';

import React from 'react';
import { AppNavbar } from './AppNavbar';
import { Footer } from './Footer';
import { HeroWireframeGlobe } from '@/components/home/HeroWireframeGlobe';

import type { GlobeMarker } from '@/components/ui/3d-globe';

export function MainLayout({
  children,
  showCreateEvent = true,
  contentClassName = 'marketing-hero mt-6',
  heroHeader,
  fixedBackground,
  showGlobeBackground = true,
  globeTopPosition = '50%',
  globeOpacity = 1.0,
  globeMarkers,
}: {
  children: React.ReactNode;
  showCreateEvent?: boolean;
  contentClassName?: string;
  /** Optional full-bleed hero band rendered behind top navbar and title */
  heroHeader?: React.ReactNode;
  /** Optional fixed background element rendered at the root layout level (z-0) */
  fixedBackground?: React.ReactNode;
  /** Whether to display the background globe (disabled) */
  showGlobeBackground?: boolean;
  /** Vertical placement of the globe center */
  globeTopPosition?: string;
  /** Optional opacity multiplier */
  globeOpacity?: number;
  /** Optional markers */
  globeMarkers?: GlobeMarker[];
}) {
  const hasHero = Boolean(heroHeader);

  return (
    <div className="relative min-h-screen overflow-x-hidden marketing-mesh-bg text-nature-text flex flex-col">
      {/* Root fixed background (e.g. HeroWireframeGlobe) */}
      {fixedBackground}

      {/* Persistent Landing Page Aesthetic (Globe + Ambient Lighting) */}
      {showGlobeBackground && (
        <div className="fixed inset-0 z-0 pointer-events-none" aria-hidden="true">
          <HeroWireframeGlobe isFixed={true} opacityMultiplier={0.35} showUntilFooter={true} />
          
          {/* Subtle warm/cool gradient glows drifting across the scroll */}
          <div className="absolute top-[3%] -left-[12%] w-[700px] h-[700px] rounded-full bg-[#B3C9D6]/20 filter blur-[180px] opacity-70" />
          <div className="absolute top-[18%] -right-[10%] w-[650px] h-[650px] rounded-full bg-[#98AA9D]/20 filter blur-[180px] opacity-70" />
          <div className="absolute top-[38%] left-[5%] w-[600px] h-[600px] rounded-full bg-[#B3C9D6]/15 filter blur-[190px] opacity-60" />
          <div className="absolute top-[58%] -right-[8%] w-[680px] h-[680px] rounded-full bg-[#98AA9D]/18 filter blur-[200px] opacity-60" />
          
          {/* Faint topographic contour svg overlay */}
          <div className="absolute inset-0 opacity-[0.035]">
            <svg className="h-full w-full" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
              <defs>
                <pattern id="main-ambient-pattern" width="64" height="64" patternUnits="userSpaceOnUse">
                  <circle cx="2" cy="2" r="1" fill="#2D3536" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#main-ambient-pattern)" />
            </svg>
          </div>
        </div>
      )}

      <AppNavbar
        showCreateEvent={showCreateEvent}
        showSearch
        isHeroHeader={hasHero}
      />

      {/* Full-width Hero Banner (e.g. HeroGlobeBand) */}
      {heroHeader && <div className="relative z-10">{heroHeader}</div>}

      {/* Main Page Content */}
      <main
        className={`relative z-10 flex-1 ${
          hasHero ? 'pt-2 sm:pt-4' : 'pt-24 md:pt-28'
        } pb-12 px-4 sm:px-6 md:pb-16 lg:px-8 ${contentClassName}`}
      >
        {children}
      </main>

      <div className="relative z-10">
        <Footer />
      </div>
    </div>
  );
}
